import apiClient from "./apiClient";

/* ── In-memory GET cache with 5-minute TTL ─────────────────────────────── */
const _cache = {};

const dedupGet = (key, fetchFn, ttl = 300000) => {
  const now = Date.now();
  if (_cache[key] && now - _cache[key].ts < ttl) return _cache[key].promise;
  const promise = fetchFn();
  _cache[key] = { promise, ts: now };
  promise.catch(() => { delete _cache[key]; });
  return promise;
};

export const clearAttendanceCache = () => {
  for (const k in _cache) delete _cache[k];
};

// ── Attendance ─────────────────────────────────────────────────────────

export const getAttendance = (params = {}) =>
  dedupGet(`attendance_${JSON.stringify(params)}`, () =>
    apiClient.get("/attendance/api/attendance/", { params })
  );

export const markAttendance = (data) => {
  return apiClient.post("/attendance/api/attendance/", data).finally(() => {
    clearAttendanceCache();
  });
};

export const editAttendance = (id, data) => {
  return apiClient.patch(`/attendance/api/attendance/${id}/`, data).finally(() => {
    clearAttendanceCache();
  });
};


// ── Location Attendance Report ─────────────────────────────────────────
export const downloadLocationAttendanceReport = (params = {}) =>
  apiClient.get("/attendance/api/attendance-report/", {
    params,
    responseType: "blob",
  });

// ── Remote Attendance ──────────────────────────────────────────────────

/**
 * Employee / Manager marks remote attendance (check_in or check_out).
 * @param {{ action: 'check_in'|'check_out', remote_reason: string, latitude?: number, longitude?: number }} data
 */
export const markRemoteAttendance = (data) => {
  return apiClient.post("/attendance/api/remote-attendance/", data).finally(() => {
    clearAttendanceCache();
  });
};

export const getRemoteAttendance = (params = {}) =>
  dedupGet(`remote_att_${JSON.stringify(params)}`, () =>
    apiClient.get("/attendance/api/remote-attendance/", { params })
  );

/**
 * Admin approves or rejects a remote attendance record.
 * @param {number} id  Attendance PK
 * @param {'approve'|'reject'} action
 */
export const resolveRemoteAttendance = (id, action) => {
  return apiClient.patch(`/attendance/api/remote-attendance/${id}/`, { action }).finally(() => {
    clearAttendanceCache();
  });
};

// ── Calendar ───────────────────────────────────────────────────────────

export const getCalendarDays = (params = {}) =>
  dedupGet(`calendar_${JSON.stringify(params)}`, () =>
    apiClient.get("/attendance/api/manage-calendar/", { params })
  );

export const updateCalendar = (data) => {
  return apiClient.post("/attendance/api/manage-calendar/", data).finally(() => {
    clearAttendanceCache();
  });
};

export const bulkUploadCalendar = (formData) => {
  return apiClient.post("/attendance/api/manage-calendar/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).finally(() => {
    clearAttendanceCache();
  });
};

// ── Calendar overrides ─────────────────────────────────────────────────

export const getOverrides = (params = {}) =>
  dedupGet(`overrides_${JSON.stringify(params)}`, () =>
    apiClient.get("/attendance/api/allow-override/", { params })
  );

export const allowOverride = (data) => {
  return apiClient.post("/attendance/api/allow-override/", data).finally(() => {
    clearAttendanceCache();
  });
};

// ── Holiday Work Requests ──────────────────────────────────────────────
export const getHolidayWorkRequests = (params = {}) =>
  dedupGet(`holiday_work_requests_${JSON.stringify(params)}`, () =>
    apiClient.get("/attendance/api/holiday-work-requests/", { params })
  );

export const actionHolidayWorkRequest = (data) => {
  return apiClient.post("/attendance/api/holiday-work-requests/", data).finally(() => {
    clearAttendanceCache();
  });
};

// ── Leave policy ───────────────────────────────────────────────────────

export const getLeavePolicies = () =>
  dedupGet("leave_policies", () =>
    apiClient.get("/attendance/api/add-leave-policy/")
  );

export const addLeavePolicy = (data) => {
  return apiClient.post("/attendance/api/add-leave-policy/", data).finally(() => {
    clearAttendanceCache();
  });
};

// ── Leaves ─────────────────────────────────────────────────────────────

export const getLeaves = (params = {}) =>
  dedupGet(`leaves_${JSON.stringify(params)}`, () =>
    apiClient.get("/attendance/api/request-leave/", { params })
  );

export const requestLeave = (data) => {
  return apiClient.post("/attendance/api/request-leave/", data).finally(() => {
    clearAttendanceCache();
  });
};

export const editLeave = (id, data) => {
  return apiClient.patch(`/attendance/api/request-leave/${id}/`, data).finally(() => {
    clearAttendanceCache();
  });
};

// ── Pending leave approval ─────────────────────────────────────────────

export const getPendingLeaves = (params = {}) =>
  dedupGet(`pending_leaves_${JSON.stringify(params)}`, () =>
    apiClient.get("/attendance/api/pending-approve-reject-leave/", { params })
  );

export const updateLeaveStatus = (data) => {
  return apiClient.post("/attendance/api/pending-approve-reject-leave/", data).finally(() => {
    clearAttendanceCache();
  });
};

// ── Edit-request tickets ───────────────────────────────────────────────

/**
 * @param {{ request_type: 'LEAVE'|'ATTENDANCE'|'PROFILE_UPDATE', reference_id?: number, reason: string, requested_changes?: object }} data
 */
export const raiseEditTicket = (data) => {
  return apiClient.post("/attendance/api/edit-requests/", data).finally(() => {
    clearAttendanceCache();
  });
};

/**
 * @param {{ status?: string, employee_id?: string }} params
 */
export const getEditTickets = (params = {}) =>
  dedupGet(`edit_tickets_${JSON.stringify(params)}`, () =>
    apiClient.get("/attendance/api/edit-requests/", { params })
  );

export const cancelEditTicket = (id) => {
  return apiClient.delete(`/attendance/api/edit-requests/${id}/`).finally(() => {
    clearAttendanceCache();
  });
};

/**
 * @param {number} id
 * @param {'approve'|'reject'} action
 * @param {string} [adminNote]
 */
export const resolveTicket = (id, action, adminNote = "") => {
  return apiClient.patch(`/attendance/api/edit-requests/${id}/`, {
    action,
    admin_note: adminNote,
  }).finally(() => {
    clearAttendanceCache();
  });
};

// ── WFH Period Requests (long-term / scheduled remote) ────────────────

/**
 * List WFH period requests.
 * @param {{ status?: string, employee_id?: string }} params
 */
export const getWfhPeriods = (params = {}) =>
  dedupGet(`wfh_periods_${JSON.stringify(params)}`, () =>
    apiClient.get("/attendance/api/wfh-periods/", { params })
  );

/**
 * Employee submits a WFH period request.
 * @param {{ from_date: string, to_date: string, reason: string }} data
 */
export const requestWfhPeriod = (data) => {
  return apiClient.post("/attendance/api/wfh-periods/", data).finally(() => {
    clearAttendanceCache();
  });
};

/**
 * Admin approves, rejects, or revokes a WFH period.
 * @param {number} id  Period PK
 * @param {'approve'|'reject'|'revoke'} action
 */
export const resolveWfhPeriod = (id, action) => {
  return apiClient.patch(`/attendance/api/wfh-periods/${id}/`, { action }).finally(() => {
    clearAttendanceCache();
  });
};

export const getTeamAttendance = (params = {}) =>
  dedupGet(`team_att_${JSON.stringify(params)}`, () =>
    apiClient.get("/attendance/api/manager/team-attendance/", { params })
  );
